import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Receipt, Plus, DollarSign, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  paid: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  unpaid: "bg-destructive/15 text-destructive",
  partial: "bg-accent/20 text-accent-foreground",
  refunded: "bg-muted text-muted-foreground",
};

export default function BillingPage() {
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");

  const [patient, setPatient] = useState("");
  const [items, setItems] = useState([{ _k: crypto.randomUUID(), description: "Consultation", quantity: 1, unit_price: 100 }]);
  const [tax, setTax] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [insuranceCovered, setInsuranceCovered] = useState(0);

  const load = useCallback(async () => {
    const [i, p] = await Promise.all([api.get("/invoices"), api.get("/patients")]);
    setInvoices(i.data); setPatients(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invoices", {
        patient_id: patient, items, tax_pct: tax, discount, insurance_covered: insuranceCovered,
      });
      toast.success("Invoice created");
      setOpen(false);
      setPatient(""); setItems([{ _k: crypto.randomUUID(), description: "Consultation", quantity: 1, unit_price: 100 }]);
      setTax(0); setDiscount(0); setInsuranceCovered(0);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const subtotal = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const total = subtotal + subtotal * (tax / 100) - discount - insuranceCovered;

  const pay = async (e) => {
    e.preventDefault();
    await api.post(`/invoices/${payOpen}/pay`, { amount: parseFloat(payAmount), method: payMethod });
    toast.success("Payment recorded");
    setPayOpen(null); setPayAmount(""); setPayMethod("cash");
    load();
  };

  return (
    <div className="space-y-5 fade-up" data-testid="billing-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="text-3xl font-bold tracking-tight">Billing & Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">{invoices.length} invoices</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="new-invoice-btn"><Plus className="size-4" /> New invoice</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label>Patient *</Label>
                <Select value={patient} onValueChange={setPatient}>
                  <SelectTrigger><SelectValue placeholder="Select patient" /></SelectTrigger>
                  <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Items</Label>
                {items.map((it, i) => (
                  <div key={it._k} className="grid grid-cols-12 gap-2">
                    <Input className="col-span-6" placeholder="Description" value={it.description}
                      onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} required />
                    <Input className="col-span-2" type="number" placeholder="Qty" value={it.quantity}
                      onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, quantity: parseInt(e.target.value) || 1 } : x))} />
                    <Input className="col-span-3" type="number" step="0.01" placeholder="Unit price" value={it.unit_price}
                      onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, unit_price: parseFloat(e.target.value) || 0 } : x))} />
                    <Button type="button" variant="ghost" size="icon" className="col-span-1" onClick={() => setItems(items.filter((_, idx) => idx !== i))} disabled={items.length === 1}>
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { _k: crypto.randomUUID(), description: "", quantity: 1, unit_price: 0 }])}>+ Add item</Button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Tax %</Label><Input type="number" value={tax} onChange={(e) => setTax(parseFloat(e.target.value) || 0)} /></div>
                <div className="space-y-1.5"><Label>Discount</Label><Input type="number" value={discount} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} /></div>
                <div className="space-y-1.5"><Label>Insurance</Label><Input type="number" value={insuranceCovered} onChange={(e) => setInsuranceCovered(parseFloat(e.target.value) || 0)} /></div>
              </div>
              <div className="p-3 bg-muted/50 rounded-md text-sm flex justify-between">
                <span>Total</span><span className="font-semibold mono">${total.toFixed(2)}</span>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit">Create invoice</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="py-3 px-5">Invoice #</th><th>Patient</th><th>Total</th><th>Balance</th><th>Status</th><th className="px-5"></th>
            </tr></thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                  <td className="py-3 px-5 mono text-xs">{i.invoice_number}</td>
                  <td className="font-medium">{i.patient_name}</td>
                  <td className="mono">${i.total?.toFixed(2)}</td>
                  <td className="mono">${i.balance?.toFixed(2)}</td>
                  <td><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[i.status] || ""}`}>{i.status}</span></td>
                  <td className="px-5 py-3 text-right">
                    {i.status !== "paid" && (
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => { setPayOpen(i.id); setPayAmount(i.balance); }} data-testid={`pay-invoice-${i.id}`}>
                        <DollarSign className="size-3.5" /> Record payment
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan="6" className="py-10 text-center text-muted-foreground">No invoices yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!payOpen} onOpenChange={(o) => !o && setPayOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
          <form onSubmit={pay} className="space-y-3">
            <div className="space-y-1.5"><Label>Amount *</Label>
              <Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} required /></div>
            <div className="space-y-1.5"><Label>Method</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["cash", "card", "insurance", "bank_transfer"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setPayOpen(null)}>Cancel</Button>
              <Button type="submit">Save payment</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
